import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { publicApi } from "@/lib/publicApi";
import { PageHero } from "@/components/public/Primitives";
import CaseStudyCard from "@/components/public/CaseStudyCard";
import CTASection from "@/components/public/CTASection";
import SEO from "@/components/public/SEO";
import { CTA } from "@/lib/ctas";
import { fadeUp, easeOutStrong } from "@/lib/motion";

export default function CaseStudies() {
    const [items, setItems] = useState(null); // null = loading, [] = empty
    useEffect(() => {
        publicApi
            .get("/case-studies")
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]));
    }, []);

    const loaded = items !== null;
    const count = loaded ? items.length : 0;

    return (
        <div data-testid="case-studies-page">
            <SEO
                title="Case Studies — Real UK Small Business Wins"
                description="See how UK takeaways, salons, retailers and trades transformed their businesses with custom websites, apps and digital systems built by Omega IT Ltd."
            />
            <PageHero
                eyebrow="Case Studies"
                title="Real outcomes for real UK small businesses."
                subtitle="A few of our recent projects — what the client needed, what we built, and what changed afterwards."
            />

            {loaded && count === 0 && (
                <section className="px-5 sm:px-8 lg:px-12 py-20" data-testid="case-studies-holding">
                    <motion.div
                        className="max-w-2xl mx-auto text-center"
                        {...fadeUp}
                    >
                        <h2 className="font-display font-black tracking-tight text-slate-950 text-3xl sm:text-4xl leading-tight">
                            Case studies coming soon
                        </h2>
                        <p className="text-base text-slate-600 mt-4 leading-relaxed">
                            We're gathering permission from clients to publish the numbers properly. In the meantime — tell us about your business.
                        </p>
                        <div className="mt-8">
                            <motion.div className="inline-block" whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} transition={{ duration: 0.2, ease: easeOutStrong }}>
                                <Link
                                    to={CTA.CONTACT.href}
                                    className="inline-flex items-center gap-1.5 bg-slate-950 hover:bg-slate-800 text-white font-semibold rounded-md px-6 py-3 text-sm"
                                    data-testid="case-studies-holding-cta"
                                >
                                    {CTA.CONTACT.label} →
                                </Link>
                            </motion.div>
                        </div>
                    </motion.div>
                </section>
            )}

            {loaded && count >= 1 && (
                <section className="px-5 sm:px-8 lg:px-12 py-20">
                    <div
                        className={`max-w-7xl mx-auto grid gap-5 ${
                            count === 1 ? "sm:grid-cols-1 max-w-2xl" : "sm:grid-cols-2 lg:grid-cols-3"
                        }`}
                    >
                        {items.map((c) => (
                            <CaseStudyCard key={c.id} caseStudy={c} />
                        ))}
                    </div>
                    {count === 1 && (
                        <p className="text-sm text-slate-500 text-center mt-8" data-testid="case-studies-more-soon">
                            More case studies coming soon.
                        </p>
                    )}
                </section>
            )}

            <CTASection />
        </div>
    );
}
